import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'

dayjs.extend(utc)

/** 东八区固定偏移，等价于 Asia/Shanghai（中国大陆无夏令时），不依赖 Intl */
const cnOffset = 8
const cnSuffix = '+08:00'

/** 趋势图 key 的格式，SQL 侧的 to_char 必须用同一个字面量 */
export const dayLabelFormat = 'MM-DD'

/** 空值占位符，表格、详情、时间格式化统一用它 */
export const emptyText = '—'

export interface Option {
  label: string
  value: string
}

/** { id, name } 列表转下拉选项 */
export function toOptions<T extends { id: string; name: string }>(
  items: T[],
): Option[] {
  return items.map((item) => ({ label: item.name, value: item.id }))
}

/** 文案表转下拉选项，keys 用来收窄可选范围 */
export function labelsToOptions<K extends string>(
  table: Record<K, string>,
  keys: readonly K[],
): Option[] {
  return keys.map((key) => ({ label: table[key], value: key }))
}

export function formatFlag(value: boolean): string {
  return value ? '是' : '否'
}

function atCN(value: Date | string | number) {
  return dayjs(value).utcOffset(cnOffset)
}

/** YYYY-MM-DD，与 SQL 侧 `at time zone 'Asia/Shanghai'` 对齐 */
export function formatDay(value: Date | string | number): string {
  return atCN(value).format('YYYY-MM-DD')
}

/** MM-DD，趋势图与日历轴的短标签 */
export function formatDayLabel(value: Date | string | number): string {
  return atCN(value).format(dayLabelFormat)
}

export function formatDateTime(value: Date | string | null): string {
  return value ? atCN(value).format(`${dayLabelFormat} HH:mm`) : emptyText
}

/** YYYY-MM-DD HH:mm，用于需要跨年份辨识的名称和记录 */
export function formatFullDateTime(value: Date | string | null): string {
  return value ? atCN(value).format('YYYY-MM-DD HH:mm') : emptyText
}

/** 新开赛事的默认名称：开局时刻加赛制名 */
export function buildDefaultGameName(
  templateName: string,
  at: Date | string = new Date(),
) {
  return `${formatFullDateTime(at)} ${templateName}`
}

/** 东八区当天 00:00:00.000，查询区间下界 */
export function startOfDay(day: string): Date {
  return dayjs(`${day}T00:00:00.000${cnSuffix}`).toDate()
}

/** 东八区当天 23:59:59.999，查询区间上界 */
export function endOfDay(day: string): Date {
  return dayjs(`${day}T23:59:59.999${cnSuffix}`).toDate()
}

/** 确认买入次数 / 在场人数，无人时占位 */
export function formatAverageHands(
  buyInCount: number,
  playerCount: number,
): string {
  if (playerCount <= 0) return emptyText
  const value = buyInCount / playerCount
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

/** 场均值，固定小数位；没有分母或字段缺失时占位 */
export function formatPerGame(
  value: number | null | undefined,
  digits: number,
): string {
  return value == null ? emptyText : value.toFixed(digits)
}

export function formatDuration(
  startedAt: Date | string | null,
  endedAt: Date | string | null,
): string {
  if (!startedAt) return '未开始'
  const minutes = Math.max(
    0,
    dayjs(endedAt ?? new Date()).diff(startedAt, 'minute'),
  )
  const hours = Math.floor(minutes / 60)
  return hours > 0 ? `${hours}小时${minutes % 60}分钟` : `${minutes}分钟`
}

function padClock(value: number) {
  return String(value).padStart(2, '0')
}

/** 支付倒计时，MM:SS */
export function formatCountdown(
  deadlineAt: Date | string,
  now: Date | string = new Date(),
): string {
  const seconds = Math.max(0, dayjs(deadlineAt).diff(now, 'second'))
  return `${padClock(Math.floor(seconds / 60))}:${padClock(seconds % 60)}`
}

/** 裁判工作台发牌计时，HH:MM:SS */
export function formatElapsedClock(
  startedAt: Date | string | null,
  endedAt: Date | string | null,
): string {
  if (!startedAt) return '00:00:00'
  const seconds = Math.max(
    0,
    dayjs(endedAt ?? new Date()).diff(startedAt, 'second'),
  )
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${padClock(hours)}:${padClock(minutes)}:${padClock(seconds % 60)}`
}

/** 整数分转人民币展示，手写格式保证小程序与 admin 逐字符一致 */
export function formatCurrency(amount: number): string {
  if (!Number.isSafeInteger(amount)) throw new RangeError('金额必须是整数分')
  const sign = amount < 0 ? '-' : ''
  const absolute = Math.abs(amount)
  const yuan = Math.floor(absolute / 100)
    .toString()
    .replace(/\B(?=(\d{3})+$)/g, ',')
  const cents = String(absolute % 100).padStart(2, '0')
  return `${sign}¥${yuan}${cents === '00' ? '' : `.${cents}`}`
}

/** 价格区间，两端相同则只展示一次 */
export function formatCurrencyRange(min: number, max: number): string {
  const low = Math.min(min, max)
  const high = Math.max(min, max)
  if (low === high) return formatCurrency(low)
  return `${formatCurrency(low)} – ${formatCurrency(high)}`
}

export function centsToYuan(amount: number): number {
  return amount / 100
}

/** 后台元输入精确转为整数分，不经过浮点乘法 */
export function parseYuanToCents(value: string | number): number {
  const normalized = String(value).trim()
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(normalized)
  if (!match) throw new RangeError('金额最多保留两位小数')
  const yuan = Number(match[1])
  const cents = Number((match[2] ?? '').padEnd(2, '0'))
  const amount = yuan * 100 + cents
  if (!Number.isSafeInteger(amount)) throw new RangeError('金额超出安全范围')
  return amount
}

export function formatRebuyLimit(limit: number | null): string {
  return limit === null ? '无上限' : String(limit)
}
